import {
  Timestamp,
  doc,
  getDoc,
  getFirestore,
  onSnapshot,
} from 'firebase/firestore';
import type { Customer, CustomerCache } from '../types/customer';

const db = getFirestore();

export const EMPTY_CUSTOMER_CACHE: CustomerCache = {
  totalCustomers: 0,
  customersList: [],
  lastUpdated: null,
};

const toDate = (value: unknown): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return undefined;
};

/**
 * Fetch a single customer by ID from companies/{companyId}/customers/{customerId}.
 * Only company admins can read this collection (see firestore.rules) - callers
 * must check hasPermission('canViewCustomers') before calling this.
 */
export const getCustomer = async (
  companyId: string,
  customerId: string
): Promise<Customer | null> => {
  try {
    const docRef = doc(db, 'companies', companyId, 'customers', customerId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      createdAt: toDate(data.createdAt) ?? new Date(),
      updatedAt: toDate(data.updatedAt) ?? null,
    } as Customer;
  } catch (error) {
    console.error('Error getting customer:', error);
    throw new Error('Failed to get customer');
  }
};

/**
 * One-shot fetch of the company's customer cache document, for manual
 * refreshCache() calls (as opposed to the live subscription below). Admin-only
 * per firestore.rules - callers must check hasPermission('canViewCustomers').
 */
export const getCustomerCache = async (
  companyId: string
): Promise<CustomerCache> => {
  try {
    const cacheRef = doc(db, 'companies', companyId, 'cache', 'customers');
    const docSnap = await getDoc(cacheRef);

    if (!docSnap.exists()) {
      return EMPTY_CUSTOMER_CACHE;
    }

    const data = docSnap.data();
    return {
      totalCustomers: data.totalCustomers ?? 0,
      customersList: data.customersList ?? [],
      lastUpdated: toDate(data.lastUpdated) ?? null,
    };
  } catch (error) {
    console.error('Error getting customer cache:', error);
    throw new Error('Failed to get customer cache');
  }
};

/**
 * Subscribe to the company's customer cache document
 * (companies/{companyId}/cache/customers). Admin-only per firestore.rules,
 * same restriction as the customers subcollection itself - callers must
 * check hasPermission('canViewCustomers') before subscribing. This is the
 * read path for any UI that needs to list/search existing customers (e.g.
 * the pool form's customer picker), mirroring subscribeToPoolCache in
 * ../api/pools.ts and the equivalent cache used by pool-guy-mobile.
 *
 * Falls back to an empty cache (rather than throwing) if the cache document
 * doesn't exist yet (e.g. a brand new company with zero customers).
 */
export const subscribeToCustomerCache = (
  companyId: string,
  callback: (cache: CustomerCache) => void,
  onError?: (error: Error) => void
): (() => void) => {
  const cacheRef = doc(db, 'companies', companyId, 'cache', 'customers');

  return onSnapshot(
    cacheRef,
    (docSnap) => {
      if (!docSnap.exists()) {
        callback({ totalCustomers: 0, customersList: [], lastUpdated: null });
        return;
      }

      const data = docSnap.data();
      callback({
        totalCustomers: data.totalCustomers ?? 0,
        customersList: data.customersList ?? [],
        lastUpdated: toDate(data.lastUpdated) ?? null,
      });
    },
    (error) => {
      console.error('Error subscribing to customer cache:', error);
      onError?.(error);
    }
  );
};
